import {
  DocumentDuplicateIcon,
  FolderIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import ShortcutChip from '@/components/shared/ShortcutChip';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { UnitOverviewCard } from '@/components/unit/UnitOverviewCard';
import { ContentLessonRef } from '@/models/Unit';
import { CourseRoute } from '@/utils/routes';
import toUnitShortcuts, {
  UnitShortcutType,
} from '@/utils/unit/toUnitShortcuts';

const shortcutIcons: Record<UnitShortcutType, ReactNode> = {
  flashcards: <RectangleStackIcon />,
  materials: <DocumentDuplicateIcon />,
  resources: <FolderIcon />,
};

export type UnitShortcutsProps = CourseRoute & {
  contentLesson: ContentLessonRef;
  hasFlashcards?: boolean;
  pendingFlashcards?: number;
};

export default function UnitShortcuts({
  contentLesson,
  hasFlashcards = false,
  pendingFlashcards = 0,
  slug,
  klassId,
}: UnitShortcutsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLessons.unitCard.shortcuts',
  });
  const shortcuts = toUnitShortcuts(
    { contentLesson, hasFlashcards, pendingFlashcards },
    { slug, klassId },
  );

  return (
    <ConditionalRenderer condition={shortcuts.length > 0}>
      <UnitOverviewCard.Shortcuts>
        {shortcuts.map(({ type, href, count }) => (
          <li key={type} className="min-w-0 flex-1 md:flex-none">
            <ShortcutChip
              icon={shortcutIcons[type]}
              label={t(type)}
              href={href}
              badge={count}
              classNames={{ base: 'md:min-w-24' }}
            />
          </li>
        ))}
      </UnitOverviewCard.Shortcuts>
    </ConditionalRenderer>
  );
}
